import { useCallback, useEffect } from 'react';

import { useUserAssets } from 'src/hooks/use-user-assets.hook';
import { Asset } from 'src/interfaces/assets.types';
import AuthService from 'src/services/api/auth.service';
import { useGetMyClassEntitlementsQuery } from 'src/store/api/retail';
import {
  setHasMiddleSchoolProduct,
  useMSProductsEntitlementsSelector,
  useSelectedClassSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';
import { hasMSGrade } from 'src/utils/assets.utils';

const MS_PRODUCTS = ['Science', 'ELA', 'Math'];

export const useMsProductCheck = () => {
  const isStudent = AuthService.isStudent();
  const selectedClass = useSelectedClassSelector();
  const hasMiddleSchoolProduct = useMSProductsEntitlementsSelector();

  const dispatch = useAppDispatch();

  const { data: userAssets, isLoadingWithoutError: isUserAssetsLoading } =
    useUserAssets();

  const { data: classesEntitlements, isLoading: isClassesEntitlementsLoading } =
    useGetMyClassEntitlementsQuery(undefined, {
      skip: isStudent,
    });

  const isLoading = isClassesEntitlementsLoading || isUserAssetsLoading;

  const searchMsProduct = useCallback((assets?: Asset[]) => {
    if (!assets) return false;

    return assets.some(
      (asset) =>
        MS_PRODUCTS.some(
          (product) =>
            asset.metadata.product && asset.metadata.product.includes(product),
        ) && hasMSGrade(asset),
    );
  }, []);

  useEffect(() => {
    if (isLoading) return;

    let relevantAssets: Asset[] | undefined;

    if (isStudent) {
      relevantAssets = userAssets;
    } else {
      if (!selectedClass) return;

      // get all classes assets when All Classes option is selected.
      if (selectedClass.length > 1) {
        relevantAssets = classesEntitlements
          ?.map((entitlement) => entitlement.programs)
          .flat()
          .map((program) => program.assets)
          .flat();
      }

      const selectedClassEntitlements = classesEntitlements?.find(
        (entitlement) => {
          return entitlement.resourceKey === selectedClass[0]?.sourcedId;
        },
      );

      relevantAssets = selectedClassEntitlements?.programs
        .map((program) => program.assets)
        .flat();
    }

    const hasMSAssets = searchMsProduct(relevantAssets);

    dispatch(setHasMiddleSchoolProduct(hasMSAssets));
  }, [
    classesEntitlements,
    userAssets,
    isStudent,
    isLoading,
    selectedClass,
    searchMsProduct,
    dispatch,
  ]);

  return { hasMiddleSchoolProduct, isLoading };
};
